import React, { useState } from "react"
import { Col, Div, Row, Text } from "atomize"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faAngleDown } from "@fortawesome/free-solid-svg-icons"
import PrimaryButton from "../buttons/PrimaryButton"
import SecondaryButton from "../buttons/SecondaryButton"
import TextInput from "../inputs/TextInput"
import { theme } from "../settings/config"
import { LevelChips } from "./NotebookVariants"
import {
	CHANGE_COPY,
	CHANGE_NEW_VARIANT,
	CHANGE_ONLY_HERE,
	CHANGE_UPDATE,
	NOTEBOOK_EXERCISE,
	NOTEBOOK_TITLE_MAX,
	isMine,
	levelLabel,
	usedLevels,
} from "../utils/notebook.utils"

// ── Hai cambiato «…» ──
// Salvando una lezione partita dal quaderno e poi cambiata, prima di
// salvare si chiede cosa farne (`notebookChanges`): nel foglio del
// programma, al posto dei campi, come la domanda prima di togliere.
//
// La prima cosa cambiata ha le sue scelte aperte, le altre (di solito gli
// esercizi) stanno chiuse sotto e dicono cosa è scelto: toccate, si aprono.
// Tutto parte da "Solo per questa lezione", così chi tocca Salva senza
// pensarci salva come prima. Di una voce propria:
// 1. solo per questa lezione: il quaderno resta com'è;
// 2. aggiorna la variante usata: le lezioni già fatte non cambiano;
// 3. crea una variante per un altro livello, già scelto se la lezione ne ha
//    uno libero. I livelli che hanno già una variante sono sbiaditi.
// Di un collega: solo per questa lezione, o una copia nel proprio quaderno.

const c = theme.colors

const initialChoices = changes =>
	Object.fromEntries(changes.map(change => [change.key, { type: CHANGE_ONLY_HERE }]))

// La variante nuova parte dal livello della lezione, se è libero
const freeLevel = (change, lessonLevel) =>
	lessonLevel && !usedLevels(change.entry).includes(lessonLevel) ? lessonLevel : null

const variantName = entry => (entry.level ? `la variante ${levelLabel(entry.level)}` : "la voce")

// Una scelta, sul bianco, col cerchio che si riempie: la usa anche il
// foglio per uscire da un circolo
export function Option({ isSelected, title, text, onSelect, children }) {
	return (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				gap: "0.625rem",
				padding: "0.75rem 0.875rem",
				borderRadius: "15px",
				background: c.surface,
				boxShadow: `inset 0 0 0 2px ${isSelected ? c.gunmetal : c.surface}`,
			}}>
			<button
				type="button"
				role="radio"
				aria-checked={isSelected}
				onClick={onSelect}
				style={{
					display: "flex",
					alignItems: "flex-start",
					gap: "0.75rem",
					border: 0,
					padding: 0,
					background: "transparent",
					font: "inherit",
					textAlign: "left",
					cursor: "pointer",
				}}>
				<span
					aria-hidden="true"
					style={{
						flex: "none",
						width: "1.25rem",
						height: "1.25rem",
						marginTop: "0.0625rem",
						borderRadius: "50%",
						boxSizing: "border-box",
						border: isSelected ? `6px solid ${c.gunmetal}` : `2px solid ${c.gunmetal_light}`,
					}}
				/>
				<span>
					<span style={{ display: "block", fontSize: "0.9375rem", fontWeight: 600, color: c.gunmetal }}>
						{title}
					</span>
					<span style={{ display: "block", fontSize: "0.8125rem", lineHeight: 1.4, color: c.text_light }}>
						{text}
					</span>
				</span>
			</button>
			{isSelected && children && <div style={{ paddingLeft: "2rem" }}>{children}</div>}
		</div>
	)
}

// Le scelte per una cosa cambiata
function ChangeChoices({ change, choice, lessonLevel, onChange }) {
	const { entry } = change
	const exercise = change.kind === NOTEBOOK_EXERCISE

	if (!isMine(entry))
		return (
			<div role="radiogroup" style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
				<Option
					isSelected={choice.type === CHANGE_ONLY_HERE}
					title="Solo per questa lezione"
					text="Nei quaderni non cambia niente."
					onSelect={() => onChange({ type: CHANGE_ONLY_HERE })}
				/>
				<Option
					isSelected={choice.type === CHANGE_COPY}
					title="Salvane una copia nel mio quaderno"
					text={`Con le sue varianti, e ${variantName(entry)} come l'hai cambiata. Solo tua, finché non la condividi.`}
					onSelect={() => onChange({ type: CHANGE_COPY, title: entry.title })}>
					<TextInput
						value={choice.title ?? ""}
						handleChange={e =>
							onChange({ ...choice, title: e.target.value.slice(0, NOTEBOOK_TITLE_MAX) })
						}
						placeholder="Nome nel tuo quaderno"
						aria-label="Nome nel tuo quaderno"
						m="0"
					/>
				</Option>
			</div>
		)

	return (
		<div role="radiogroup" style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
			<Option
				isSelected={choice.type === CHANGE_ONLY_HERE}
				title="Solo per questa lezione"
				text="Nel quaderno resta com'è."
				onSelect={() => onChange({ type: CHANGE_ONLY_HERE })}
			/>
			<Option
				isSelected={choice.type === CHANGE_UPDATE}
				title={entry.level ? `Aggiorna la variante ${levelLabel(entry.level)}` : "Aggiorna il quaderno"}
				text="Da qui in avanti si parte da questa. Le lezioni già fatte non cambiano."
				onSelect={() => onChange({ type: CHANGE_UPDATE })}
			/>
			<Option
				isSelected={choice.type === CHANGE_NEW_VARIANT}
				title="Crea una variante"
				text={`La stessa ${exercise ? "cosa" : "lezione"}, per un altro livello. ${
					entry.level ? `La variante ${levelLabel(entry.level)} resta com'è.` : "Quella di prima resta com'è."
				}`}
				onSelect={() =>
					onChange({ type: CHANGE_NEW_VARIANT, level: freeLevel(change, lessonLevel) })
				}>
				<LevelChips
					value={choice.level ?? null}
					used={usedLevels(entry)}
					ground={c.background}
					onChange={level => onChange({ ...choice, level })}
				/>
				<Text textSize="caption" textColor={c.text_light} m={{ t: "0.5rem" }}>
					{choice.level && choice.level === lessonLevel
						? `Già scelto il ${levelLabel(lessonLevel)}, il livello di questa lezione. `
						: ""}
					Sbiaditi i livelli che hanno già la loro variante.
				</Text>
			</Option>
		</div>
	)
}

const choiceLabel = (choice, entry) =>
	choice.type === CHANGE_UPDATE
		? entry.level
			? `Aggiorna la variante ${levelLabel(entry.level)}`
			: "Aggiorna il quaderno"
		: choice.type === CHANGE_NEW_VARIANT
			? choice.level
				? `Nuova variante ${levelLabel(choice.level)}`
				: "Nuova variante: scegli il livello"
			: choice.type === CHANGE_COPY
				? "Una copia nel mio quaderno"
				: "Solo per questa lezione"

const heading = change =>
	change.kind === NOTEBOOK_EXERCISE
		? `Hai cambiato l'esercizio «${change.entry.title}»`
		: `Hai cambiato «${change.entry.title}»`

const provenance = entry => {
	const level = entry.level ? `, variante ${levelLabel(entry.level)}` : ""
	return isMine(entry)
		? `Dal tuo quaderno${level}.`
		: `È di ${entry.authorName || "un collega"}${level}: la sua resta com'è.`
}

// Quello che si sceglie arriva a `onConfirm` come { [change.key]: scelta }
function NotebookChanges({ changes, lessonLevel, busy, onConfirm, onBack }) {
	const [choices, setChoices] = useState(() => initialChoices(changes))
	const [expanded, setExpanded] = useState(changes[0]?.key)
	const [first, ...others] = changes
	// niente da chiedere: chi apre il foglio non lo apre vuoto, ma un foglio
	// vuoto non deve fermare l'app
	if (!first) return null

	const isReady = changes.every(change => {
		const choice = choices[change.key] ?? { type: CHANGE_ONLY_HERE }
		if (choice.type === CHANGE_NEW_VARIANT) return Boolean(choice.level)
		if (choice.type === CHANGE_COPY) return (choice.title ?? "").trim() !== ""
		return true
	})

	const choose = key => choice => setChoices(current => ({ ...current, [key]: choice }))

	return (
		<Div>
			<Text textSize="title" textWeight="bold" textColor={c.gunmetal}>
				{heading(first)}
			</Text>
			<Text textColor={c.text_light} m={{ b: "0.75rem" }}>
				{provenance(first.entry)}
			</Text>
			<Div maxH="56vh" style={{ overflowY: "auto", overflowX: "hidden" }}>
				{expanded === first.key ? (
					<ChangeChoices
						change={first}
						choice={choices[first.key]}
						lessonLevel={lessonLevel}
						onChange={choose(first.key)}
					/>
				) : (
					<ClosedChange
						change={first}
						choice={choices[first.key]}
						lead=""
						onOpen={() => setExpanded(first.key)}
					/>
				)}
				{others.map(change =>
					expanded === change.key ? (
						<Div key={change.key} m={{ t: "1rem" }}>
							<Text textSize="caption" textColor={c.text_light} m={{ b: "0.375rem", l: "0.25rem" }}>
								E {change.kind === NOTEBOOK_EXERCISE ? "l'esercizio" : "la lezione"} «
								{change.entry.title}» · {provenance(change.entry)}
							</Text>
							<ChangeChoices
								change={change}
								choice={choices[change.key]}
								lessonLevel={lessonLevel}
								onChange={choose(change.key)}
							/>
						</Div>
					) : (
						<ClosedChange
							key={change.key}
							change={change}
							choice={choices[change.key]}
							onOpen={() => setExpanded(change.key)}
						/>
					),
				)}
			</Div>
			<Div m={{ x: "1rem" }}>
				<Row m={{ t: "1rem" }}>
					<Col p="0">
						<SecondaryButton
							m="auto"
							textColor={c.gunmetal}
							text="Indietro"
							textSize="subheader"
							disabled={busy}
							action={onBack}
						/>
					</Col>
					<Col p="0">
						<PrimaryButton
							m="auto"
							bg={c.primary}
							textColor="white"
							textSize="subheader"
							text={busy ? "Salvo..." : "Salva"}
							disabled={busy || !isReady}
							action={() => onConfirm(choices)}
						/>
					</Col>
				</Row>
			</Div>
		</Div>
	)
}

const capitalized = text => text.charAt(0).toUpperCase() + text.slice(1)

// Una cosa cambiata, chiusa: cosa è e cosa si è scelto. Toccata si apre.
function ClosedChange({ change, choice, lead = "E ", onOpen }) {
	return (
		<button
			type="button"
			onClick={onOpen}
			style={{
				display: "flex",
				alignItems: "center",
				gap: "0.75rem",
				width: "100%",
				marginTop: "0.75rem",
				padding: "0.75rem 0.875rem",
				border: 0,
				borderRadius: "15px",
				background: c.surface,
				font: "inherit",
				textAlign: "left",
				cursor: "pointer",
			}}>
			<span style={{ flex: 1, minWidth: 0 }}>
				<span style={{ display: "block", fontSize: "0.8125rem", color: c.text_light }}>
					{capitalized(`${lead}${change.kind === NOTEBOOK_EXERCISE ? "l'esercizio" : "la lezione"}`)} «
					{change.entry.title}»
				</span>
				<span style={{ display: "block", fontSize: "0.875rem", fontWeight: 600, color: c.gunmetal }}>
					{choiceLabel(choice, change.entry)}
				</span>
			</span>
			<FontAwesomeIcon icon={faAngleDown} color={c.text_light} />
		</button>
	)
}

export default NotebookChanges
